// mantine
import { Button, TextInput, UnstyledButton } from '@mantine/core';
import { useFocusTrap } from '@mantine/hooks';
// utils
import { useFormik } from 'formik';
import type { TSkillParameter, TSkillParameterData } from '@newo/api/gateway';
import { processFormData } from '@newo/toolkit/bff';
import validationSchema from '@/modules/Designer/components/Parameters/components/ParameterForm/ParameterForm.schema.ts';

interface ParameterFormProps {
    parameter?: TSkillParameter;
    onCancel?: () => void;
    onSubmit: (
        data: TSkillParameterData,
        parameterId?: string
    ) => Promise<unknown>;
}

const ParameterForm = ({
    parameter,
    onCancel,
    onSubmit
}: ParameterFormProps) => {
    const focusTrapRef = useFocusTrap(!!parameter);

    const initialValues: TSkillParameterData = {
        name: parameter?.name ?? '',
        default_value: parameter?.default_value ?? ' '
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (formData) => {
            const data = processFormData(formData);
            try {
                await onSubmit(data, parameter?.id);
                formik.setSubmitting(false);
                onCancel?.();
            } catch {
                formik.setSubmitting(false);
            }
        }
    });

    const handleCancel = () => {
        onCancel?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.name && formik.values.name
                        ? formik.errors.name
                        : undefined
                }
                label="Parameter name"
                name="name"
                placeholder="Parameter name"
                value={formik.values.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <TextInput
                error={
                    formik.touched.default_value && formik.values.default_value
                        ? formik.errors.default_value
                        : undefined
                }
                label="Parameter default value"
                name="default_value"
                placeholder="Default parameter value"
                ref={focusTrapRef}
                value={formik.values.default_value}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {parameter ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default ParameterForm;
